import { Component, EventEmitter, Input, Output, NgZone, OnDestroy, OnInit, OnChanges, SimpleChanges,
    SecurityContext } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import {
    ITCIAppCustomizationParam,
    ITCIApplication,
    ITCIApplicationDeleteRequest,
    ITCITenantAppCategory,
    ITCIAssetTag,
    TCI_APP_CUSTOMIZATION_TYPE,
    TCI_APP_FEATURE_TYPE,
    TCI_COMPONENT_MODE,
    TCIApplicationDeploymentMode,
    TCIMainMenu,
    TCIMainMenuSubroutes,
    TCITenantAppCategory,
    TCITenantAppDetailsMenu,
    TCIApplicationDeploymentNotification,
    TCITenantSelectorType
} from "@tci/common";
import { TciTenantService } from "../../../ng-tenant/tci-tenant.service";
import { NgAppListService } from "../../ng-app-list.service";
import { NgUserService } from "../../../ng-shared/ng-services/ng-user.service";
import { NgAppFeatureToggleService } from "../../../ng-shared/ng-services/ng-app-feature-toggle.service";
import { NgTagsService } from "../../../ng-shared/ng-services/ng-tags.service";
import { NgAppChangeNotificationService } from "../../../ng-shared/ng-services/ng-app-change-notification.service";
import { NgAppHMService } from "../../../ng-app-hamburger-menu/ng-app-hamburger-menu.service";
import { ITCITenantServices, TciTenantModelService } from "../../../ng-tenant/tci-tenant.model";
import { BaseAppDetailMenuComponentInput } from "../../../ng-app-details/ng-app-details-menu";
import {
    ITCITenantCacheOptions,
    TciTenantComponentCacheService
} from "../../../ng-tenant/tci-tenant-component-cache.service";
import { TCITenantMenuServices } from "../../../ng-tenant/tci-tenant.menu";
import { InlineMenuItemHandlerFunction } from "../../../ng-tenant/tci-tenant-component";
import { setTimeoutInZone } from "../../../ng-shared/ng-util/tools";

import { union } from "lodash";
import { Subscription } from "rxjs";

@Component({
    selector: "ng-app-listing-row",
    templateUrl: "./ng-app-listing-row.component.html",
    styleUrls: ["./ng-app-listing-row.component.css"]
})
export class NgAppListingRowComponent implements OnInit, OnChanges, OnDestroy {
    @Input() app: ITCIApplication;
    @Input() tags: ITCIAssetTag[];
    @Input() customConfig: { [key: string]: ITCIAppCustomizationParam } = {};
    @Input() peekViewEnabled: boolean;
    @Input() showHamburger: boolean;
    @Output() Delete = new EventEmitter();

    category: ITCITenantAppCategory;
    tenant: ITCITenantServices;
    appDeployMode = TCIApplicationDeploymentMode.MODE_APP_LIST;
    appDetailsRoute: { route: string[], queryParams: Object };

    showStatusMenu = false;
    statusMenuAvailable = false;
    statusMenuOptions: TCITenantAppDetailsMenu[] = [];
    statusMenuOptionsInlineFns: { [key: string]: InlineMenuItemHandlerFunction<any, any, any> } = {};

    subscriptions: Subscription[] = [];
    instances: number;

    canDelete = false;
    scaleIsWorking = false;

    // Custom Configs
    private CUSTOM_TYPES = TCI_APP_CUSTOMIZATION_TYPE;

    // App Peek
    private monitorInitialized = false;
    monitorEnabled = false;
    loadingAppPeek = false;
    showAppPeek = false;
    ispeekViewCmpExist = false;
    appPeekInput: BaseAppDetailMenuComponentInput;

    // Template values
    rowId: string;
    dateColTCI: boolean;

    // Tags
    appTagsInfo = "";

    // Tooltips
    showNameTooltip = false;
    tooltipOffsetLeft = "0";

    constructor(
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        private cacheSvc: TciTenantComponentCacheService,
        private tenantMenuSrv: TCITenantMenuServices,
        private appListSrv: NgAppListService,
        private userSvc: NgUserService,
        private router: Router,
        private route: ActivatedRoute,
        private featureToggleSvc: NgAppFeatureToggleService,
        private appRefreshNotifSvc: NgAppChangeNotificationService,
        private tagsService: NgTagsService,
        private hmSvc: NgAppHMService,
        private sanitize: DomSanitizer,
        private ngZone: NgZone
    ) {
        this.featureToggleSvc
            .getAppFeatures()
            .subscribe(res => this.monitorEnabled = res[TCI_APP_FEATURE_TYPE.APP_MONITORING].enabled);

        this.subscriptions.push(
            this.appRefreshNotifSvc.getRefreshSubject()
                .filter(refr => refr.app.id === this.app.id)
                .subscribe(() => {
                    this.getAppPeekInput();
                })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if ("tags" in changes && this.tags && this.tags.length && this.tags.length > 0) {
            this.appTagsInfo = this.tagsService.concatTagNames(this.tags);
        } else {
            this.appTagsInfo = undefined;
        }
    }

    ngOnInit() {
        this.category = TCITenantAppCategory.fromApplication(this.app);
        this.appDetailsRoute = this.appListSrv.getAppDetailsRoute(this.app);
        this.instances = this.app.desiredInstanceCount;
        this.setRowId();
        this.setDateColTCI();

        this.subscriptions.push(
            this.hmSvc.getAppMenuOptionsSubject(this.app) // Listen for HM changes
                .subscribe(opts => this.setStatusMenuOptions(opts)),

            this.hmSvc.getAppMenuOptions(this.app, TCI_COMPONENT_MODE.COMP_MODE_APP_LIST)
                .subscribe() // Initialize HM options
        );

        // Initialize service for "canDelete"
        this.tenantSvc.onInit()
            .subscribe(() => {
                const tenant = this.tenantModelSvc.getTenant(this.app.type);
                if (tenant) {
                    this.tenant = tenant;
                    this.updateLastRunTime();
                    this.statusMenuAvailable = this.app && !this.app.onPremise;
                    this.checkCanDelete();
                    this.getAppPeekInput();
                }
            });

        const cacheOpts: ITCITenantCacheOptions = {
            routeId: TCIMainMenu.MAIN_MENU_APPLICATIONS.getRouteId(),
            subRouteId: TCIMainMenuSubroutes.TCI_APPLICATION_APP_LIST.getSubRouteId(),
            tenantType: this.app.type
        };

        this.cacheSvc.getCache(false, cacheOpts)
            .subscribe((cache) => {
                this.ispeekViewCmpExist = !!cache[`${this.app.type.toString()}:${TCITenantSelectorType.TCI_APP_LIST_MONITORING_APP_PEEK.toString()}`];
            });
    }

    ngOnDestroy() {
        this.appRefreshNotifSvc.unsetAuditsData(this.app);
        this.hmSvc.removeHMSubject(this.app);
        this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    private updateLastRunTime() {
        const deployprovider = this.tenant.getAppDeploymentProvider();
        if (deployprovider) {
            this.subscriptions.push(
                deployprovider.getStatusSubject()
                    .filter(res => res.appId === this.app.id && res.lastRunTime !== this.app.lastRunTime && res.lastRunTime > 0)
                    .filter(res =>
                        [
                            TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH,
                            TCIApplicationDeploymentNotification.NOTIFICATION_STATUS
                        ].includes(res.notificationType)
                    )
                    .subscribe(res => {
                        this.app.lastRunTime = res.lastRunTime;
                    })
            );
        }
    }

    /**
     * IPAS-14801 - rzavala 5 aug 2020
     * The HM items collection is comming well formed, but for some reason the template does
     * not display them from time to time. So I'm wrapping the collection in ngZone to ensure
     * the render detects the change. Also I added a "trackBy" setting at the ngFor trying
     * to enforce the collection change detection.
     */
    private setStatusMenuOptions(opts: TCITenantAppDetailsMenu[]) {
        setTimeoutInZone(this.ngZone, () => {
            this.statusMenuOptions = union(this.statusMenuOptions, opts);
            this.statusMenuAvailable = opts && opts.length > 0;

            // Feed the inline HM items collection
            const preloadMap = this.tenantMenuSrv.getTenantPreloadMap(this.app.type);
            this.statusMenuOptions
                .forEach(opt => {
                    const selStr = opt.getComponentSelectorType().toString();

                    // Identify inline components
                    this.statusMenuOptionsInlineFns[selStr] = preloadMap[selStr] && preloadMap[selStr].inlineMenuHandlerFn;
                });
        });
    }

    // IPAS-14801 - https://stackoverflow.com/questions/49380181/angular-2-ngfor-not-updating-view
    statusMenuTrack(index: number, opt: TCITenantAppDetailsMenu) {
        return `hm-item-${opt.getRouteId()}-${index}`;
    }

    setRowId() {
        this.rowId = "app_row_" + this.app.id;
    }

    goToAppDetails($event: MouseEvent, externalUrl: string) {
        if (externalUrl) {
            window.open(this.sanitize.sanitize(SecurityContext.URL, externalUrl), "_blank");
            $event.preventDefault();
        } else {
            this.router.navigate(this.appDetailsRoute.route, {
                queryParams: this.appDetailsRoute.queryParams,
                relativeTo: this.route
            });
        }
    }

    private checkCanDelete() {
        if (this.tenant) {
            const appData: ITCIApplicationDeleteRequest = {
                application: this.app,
                appId: this.app.id,
                tenantType: this.app.type
            };
            this.subscriptions.push(
                this.tenant.getAppDeleteProvider()
                    .canDeleteApplication(appData)
                    .subscribe(canDelete => this.canDelete = canDelete)
            );
        } else {
            this.canDelete = false;
        }
    }

    deleteApp($event: MouseEvent) {
        if (this.canDelete && !this.app.onPremise) {
            this.Delete.emit(this.app);
        }
        $event.stopPropagation();
    }

    toggleStatusMenu($event: MouseEvent) {
        this.showStatusMenu = !this.showStatusMenu;
        $event.stopPropagation();
    }

    // Update the instances count
    setInstances(num: number) {
        this.instances = num;
    }

    private scaleWorking(scaling: boolean) {
        this.scaleIsWorking = scaling;
        this.checkCanDelete();
    }

    // Date rows switch
    setDateColTCI() {
        this.dateColTCI = this.app.modifiedTime > 0;
    }

    // Customizer
    getCustomVisible(type: TCI_APP_CUSTOMIZATION_TYPE): boolean {
        if (!this.customConfig[type]) {
            return false; // As default
        }
        return this.customConfig[type].enabled && this.customConfig[type].visible;
    }

    // App Peek
    getAppPeekInput() {
        if (this.app.info.isMetricsEnabled) {
            /**
             * Refresh notification does not include isMetricsEnabled data, but that
             * can not change once it's true, so I use this variable to avoid removing
             * the app-peek button
             */
            this.monitorInitialized = true;
            this.monitorEnabled = true;
        } else if (!this.monitorInitialized) {
            this.monitorEnabled = false;
        }
    }

    showAppPeekToggle() {
        this.showAppPeek = !this.showAppPeek;
        if (this.showAppPeek) {
            this.loadingAppPeek = true;
            const opts: ITCITenantCacheOptions = {
                routeId: TCIMainMenu.MAIN_MENU_APPLICATIONS.getRouteId(),
                subRouteId: TCIMainMenuSubroutes.TCI_APPLICATION_APP_LIST.getSubRouteId(),
                tenantType: this.app.type
            };
            if (!this.appPeekInput) {
                this.cacheSvc.getCache(false, opts)
                    .subscribe(() => {
                        this.loadingAppPeek = false;
                        this.appPeekInput = new BaseAppDetailMenuComponentInput(
                            this.app.type,
                            TCITenantSelectorType.TCI_APP_LIST_MONITORING_APP_PEEK,
                            this.app,
                            this.userSvc,
                            this.featureToggleSvc,
                            TCI_COMPONENT_MODE.COMP_MODE_APP_LIST
                        );
                    });
            } else {
                this.loadingAppPeek = false;
            }
            this.appRefreshNotifSvc.setAuditsData(this.app);
        } else {
            this.appRefreshNotifSvc.unsetAuditsData(this.app);
        }
    }

    executeInlineCompFn($event: MouseEvent, opt: TCITenantAppDetailsMenu) {
        const selector = opt.getComponentSelectorType();
        const selStr = selector.toString();
        const tenantHMInput = new BaseAppDetailMenuComponentInput(
            this.app.type,
            selector,
            this.app,
            this.userSvc,
            this.featureToggleSvc,
            TCI_COMPONENT_MODE.COMP_MODE_APP_DETAIL_MENU,
            opt,
            null
        );
        const injector = this.tenantMenuSrv.getTenantInjector(this.app.type);
        this.statusMenuOptionsInlineFns[selStr](opt, tenantHMInput, injector).subscribe();
        this.toggleStatusMenu($event);
    }

    // Name tooltip
    showTooltip($event: any) {
        // "10" is to adjust the chevron and make it point to the label icon
        this.tooltipOffsetLeft = ($event.target.offsetLeft - 10) + "px";
        this.showNameTooltip = true;
    }

    hideTooltip() {
        this.showNameTooltip = false;
    }
}
